import React, { useState } from 'react';
import {
  CustomerForm,
  CustomerFormProvider,
  useCustomerForm,
  ActionHeader,
  CustomerFormSourceMapping,
  CustomerFormDeal,
  CustomerInfoConfig,
} from '@upstart/shared-components';
import { Dealer } from '@upstart/harmony/lib/legos/dealerTypes';
import { CrmType } from '@upstart/harmony/lib/constants';
import ReactModal from 'react-modal';
import { Deal } from '@upstart/harmony/lib/legos/dealTypes';
import { useCounties } from 'hooks/useCounties';
import { useAddress } from 'hooks/GoogleAddress/useAddresses';
import './CustomerFormPanel.scss';
import omit from 'lodash/omit';
import { MessageType } from '@upstart/harmony/lib/legos/websiteTypes';
import { PageMessage } from 'components/GlobalUI';
import { isMarketScanIncentivesEnabled } from '@upstart/harmony/lib/dealer-utils';
import {
  PAGE_MESSAGE_BODY_FOR_ZIP_CODE_CHANGE,
  PAGE_MESSAGE_HEADER_FOR_ZIP_CODE_CHANGE,
} from './constants';

type CustomerFormPanelProps = {
  deal: Deal;
  dealer: Dealer;
  sourceMapping: CustomerFormSourceMapping;
  onClose: () => void;
  updateDeal: (deal: Deal) => Promise<void>;
  isLoading?: boolean;
};

/**
 *  This condition checks for four scenarios regarding the applicant's ZIP code in a deal:
 *  1. If the dealer has MarketScan incentives enabled.
 *  2. If the updated deal has an applicant ZIP code.
 *  3. If the original deal does not have an applicant ZIP code and the updated deal's applicant ZIP code is different from the dealer's ZIP code.
 *     We need to check this because the dealer's ZIP code is used to determine the available incentives as default and we need to compare this for the first time only.
 *  4. If the original deal's applicant ZIP code is different from the updated deal's applicant ZIP code.
 */
function shouldDisplayToastMessageForZipCodeChange({
  originalDeal,
  updatedDeal,
  dealer,
}: {
  originalDeal: Deal;
  updatedDeal: CustomerFormDeal;
  dealer: Dealer;
}): boolean {
  if (!isMarketScanIncentivesEnabled(dealer)) {
    return false;
  }

  if (!updatedDeal.applicant?.zip) {
    return false;
  }

  return (
    (!originalDeal.applicant?.zip &&
      updatedDeal.applicant?.zip !== dealer.zip) ||
    (!!originalDeal.applicant?.zip &&
      originalDeal.applicant?.zip !== updatedDeal.applicant?.zip)
  );
}

/**
 * Displays a toast message indicating a change in the ZIP code.
 * This function utilizes the `PageMessage` component to show a toast with predefined headline and message
 * specific to ZIP code changes. The toast is of type 'INFO' and automatically closes after 6000 milliseconds.
 */
const displayToastMessageForZipCode = () => {
  PageMessage({
    headline: PAGE_MESSAGE_HEADER_FOR_ZIP_CODE_CHANGE,
    message: PAGE_MESSAGE_BODY_FOR_ZIP_CODE_CHANGE,
    type: MessageType.INFO,
    overrideToastOptions: {
      autoClose: 6000,
    },
  });
};

/**
 * Displays a toast message for a ZIP code change if necessary.
 *
 * This function checks if a toast message should be displayed for a ZIP code change by comparing the original
 * and updated deal information in the context of a specific dealer. If the conditions for displaying the toast
 * message are met, it triggers the display of a toast message.
 *
 * @param {Object} params - The parameters for checking the necessity of displaying a toast message.
 * @param {Deal} params.originalDeal - The original deal before any updates.
 * @param {CustomerFormDeal} params.updatedDeal - The updated deal after changes have been made.
 * @param {Dealer} params.dealer - The dealer associated with the deal.
 */
const displayToastMessageForZipCodeIfNecessary = ({
  originalDeal,
  updatedDeal,
  dealer,
}: {
  originalDeal: Deal;
  updatedDeal: CustomerFormDeal;
  dealer: Dealer;
}): void => {
  const shouldDisplayToastMessage = shouldDisplayToastMessageForZipCodeChange({
    originalDeal,
    updatedDeal,
    dealer,
  });

  if (shouldDisplayToastMessage) {
    displayToastMessageForZipCode();
  }
};

const ProviderWrappedContent: React.FC<CustomerFormPanelProps> = ({
  deal,
  dealer,
  sourceMapping,
  updateDeal,
  isLoading,
  onClose,
}) => {
  const { submit, close, formMode } = useCustomerForm();

  const applicant = deal?.applicant ?? {};
  const isEditMode = formMode === 'edit';
  let nameLabel = '';
  if (isEditMode) {
    nameLabel = `${applicant.firstName || ''} ${applicant.lastName || ''}`;
  }

  const [currentZip, setCurrentZip] = useState(applicant.zip ?? '');
  const { data: counties, error: countiesError } = useCounties({
    dealerId: dealer.id,
    zipCode: currentZip,
  });
  const zipDoesNotExist = !!countiesError;

  const [currentStreetAddress, setCurrentStreetAddress] = useState(
    applicant.address ?? '',
  );
  const { data: addressHook } = useAddress(currentStreetAddress);

  const resetCreditApplication = (): void => {
    const signaturesToRemove = [
      'creditApplicationSignature',
      'applicantSpouseSignature',
      'coapplicantSpouseSignature',
      'dealSummarySignature',
    ] as const;
    const attachedFiles = omit(deal?.attachedFiles, signaturesToRemove);

    updateDeal({
      id: deal?.id,
      car_id: deal?.car_id,
      dealer_id: deal?.dealer_id,
      attachedFiles,
      submittedToLendersAt: null,
    });
  };

  return (
    <div data-testid="customer-form-panel">
      <ActionHeader
        titleText={isEditMode ? nameLabel : 'Add customer info'}
        actionButtonText={nameLabel ? '' : 'Save'}
        cancelButtonText={nameLabel ? '' : 'Cancel'}
        onClose={close}
        submitForm={submit}
        isLoading={isLoading}
      />
      <div style={{ padding: '24px 32px' }}>
        {dealer?.crmType && (
          <div>
            <CustomerForm
              deal={deal}
              onZipChange={zip => setCurrentZip(zip)}
              counties={counties}
              dealerCrmType={dealer.crmType as CrmType}
              dealerSourceMapping={sourceMapping}
              onAddressChange={streetAddress => {
                setCurrentStreetAddress(streetAddress);
              }}
              addressFinderOptions={addressHook?.options ?? []}
              onEditSave={async (updatedDeal: CustomerFormDeal) => {
                updateDeal({
                  id: deal?.id,
                  car_id: deal?.car_id,
                  dealer_id: deal?.dealer_id,
                  ...updatedDeal,
                });

                displayToastMessageForZipCodeIfNecessary({
                  originalDeal: deal,
                  updatedDeal,
                  dealer,
                });
                onClose();
              }}
              customerInfoConfig={
                dealer.tabletFormsConfig?.customerInfo as CustomerInfoConfig
              }
              creditAppUrl={`${process.env.REACT_APP_INSTORE_URL}/credit-application`}
              onResetCreditApp={resetCreditApplication}
              validations={{ applicant: { zipDoesNotExist } }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const customModalStyles = {
  content: {
    width: '600px',
    top: '0',
    left: 'calc(100% - 600px)',
    bottom: '0',
    background: '#FAFAFA',
    borderRadius: '0',
    padding: '0',
    border: 'none',
    transform: 'translateX(0)',
    transition: 'all 200ms ease-in-out',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: '0',
  },
};

const CustomerFormPanel: React.FC<CustomerFormPanelProps> = props => {
  const { deal, onClose, updateDeal, dealer } = props;
  const [dealLoading, setDealLoading] = useState(false);
  const handleSubmit = async (updatedDeal: CustomerFormDeal) => {
    setDealLoading(true);

    displayToastMessageForZipCodeIfNecessary({
      originalDeal: deal,
      updatedDeal,
      dealer,
    });

    await updateDeal({
      id: deal?.id,
      car_id: deal?.car_id,
      dealer_id: deal?.dealer_id,
      ...updatedDeal,
    });
    onClose();
  };

  return (
    <ReactModal isOpen style={customModalStyles} ariaHideApp={false}>
      <CustomerFormProvider onClose={onClose} onSubmit={handleSubmit}>
        <ProviderWrappedContent {...props} isLoading={dealLoading} />
      </CustomerFormProvider>
    </ReactModal>
  );
};

export default CustomerFormPanel;
